import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { TableCell, TableRow } from '@this/src/components/shared/ui/data_displays/table';
import { Link } from '@this/src/components/shared/ui/navigations/link';
import { Button } from '@this/components/shared/ui/inputs/button';
import { Checkbox } from '@this/src/components/shared/ui/inputs/checkbox';
import { Fetcher } from '@this/src/util';
import Notification from '@this/src/notification';
import type EanHotelCancelInventoryItem from '@this/src/domain/arrangement/ean_hotel_cancel_inventory_item';
import type { EanHotelCancelItemStatusArgs } from '@this/src/domain/arrangement/ean_hotel_cancel_inventory_item';
import { eanHotelCancelCommentTypeLabels } from '@this/src/domain/arrangement/ean_hotel_cancel_inventory_item';
import { OrderItemJobType } from '@this/src/domain/order_item/order_item_job_type';
import type { OrderItemStatusKey } from '@this/src/domain/order_item/order_item_status';
import { OrderItemStatus } from '@this/src/domain/order_item/order_item_status';
import CancelInventoryCommentModal from './cancel_inventory_comment_modal';

interface Props {
  item: EanHotelCancelInventoryItem;
  checked: boolean;
  onInventory: () => void;
  onToggle: (checked: boolean) => void;
}

const PINK_BG = '#ffe0e6';
const SUB_HEADER_BG = '#f5f5f5';
const BADGE_STYLE: React.CSSProperties = {
  display: 'inline-block',
  marginTop: '4px',
  padding: '1px 6px',
  borderRadius: '10px',
  fontSize: '11px',
  background: '#eee',
  color: '#555'
};

const formatYen = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '─';
  return `¥${value.toLocaleString()}`;
};

const itemStatusLabel = (itemStatus: EanHotelCancelItemStatusArgs | null): string => {
  if (!itemStatus) return '─';
  const jobTypeLabel = itemStatus.jobType === null ? '' : OrderItemJobType[itemStatus.jobType];
  const statusLabel = OrderItemStatus[itemStatus.status as OrderItemStatusKey] ?? String(itemStatus.status);
  return `${jobTypeLabel}-${statusLabel}`;
};

const CancelTableRow = observer(({ item, checked, onInventory, onToggle }: Props) => {
  const [loading, setLoading] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);

  const handleInventory = async () => {
    if (loading) return;
    try {
      setLoading(true);
      await Fetcher.put(`/arrangement/auto_reservation_inventory/ean_hotel_cancel/items/${item.id}/inventory`, {});
      Notification.success('棚卸を実行しました');
      onInventory();
    } catch {
      Notification.error('棚卸に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelInventory = async () => {
    if (loading) return;
    try {
      setLoading(true);
      await Fetcher.put(
        `/arrangement/auto_reservation_inventory/ean_hotel_cancel/items/${item.id}/cancel_inventory`,
        {}
      );
      Notification.success('棚卸を取消しました');
      onInventory();
    } catch {
      Notification.error('棚卸取消に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const rowBg = item.needsAttention ? PINK_BG : undefined;
  const amountCellBg = item.amountMismatched ? PINK_BG : undefined;

  return (
    <TableRow style={{ background: rowBg }}>
      <TableCell>
        <Checkbox
          checked={checked}
          disabled={!item.bulkInventoriable}
          onChange={e => onToggle(e.target.checked)}
        />
      </TableCell>
      <TableCell>{itemStatusLabel(item.originalItem)}</TableCell>
      <TableCell>{itemStatusLabel(item.cancelChargeItem)}</TableCell>
      <TableCell style={{ fontWeight: item.apiUncanceled ? 'bold' : 'normal' }}>
        {item.apiCanceled ? 'キャンセル済' : '予約済み'}
        {item.lastErrorMessage && <div style={{ fontSize: '11px', color: '#c00' }}>{item.lastErrorMessage}</div>}
        {item.cancelChargeSourceLabel && <div style={BADGE_STYLE}>{item.cancelChargeSourceLabel}</div>}
        {item.sameRoomTypeAmbiguousLabel && <div style={BADGE_STYLE}>{item.sameRoomTypeAmbiguousLabel}</div>}
      </TableCell>
      <TableCell>{item.itineraryIds.length > 0 ? item.itineraryIds.join(', ') : '─'}</TableCell>
      <TableCell>
        <Link
          href={`/arrangement/virtual_counter?trip_id=${item.tripId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {item.tripId}
        </Link>
      </TableCell>
      <TableCell>{item.originalItem?.traceId ?? '─'}</TableCell>
      <TableCell>{item.cancelChargeItem?.traceId ?? '─'}</TableCell>
      <TableCell>{item.hotelName || '─'}</TableCell>
      <TableCell>{item.checkinDate || '─'}</TableCell>
      <TableCell>{item.checkoutDate || '─'}</TableCell>
      {/* 宿泊代金・キャンセル料: 縦2ブロック（上=右ナビ / 下=EAN側。左右分割しない） */}
      <TableCell style={{ background: amountCellBg, padding: 0 }}>
        <div style={{ padding: '6px 10px', borderBottom: '1px dashed #ccc', background: SUB_HEADER_BG }}>
          <div style={{ fontSize: '11px', color: '#666' }}>右ナビ</div>
          <div>
            宿泊: {formatYen(item.amounts.orderItem.hotelPrice)}
            {' / '}
            キャンセル料: {formatYen(item.amounts.orderItem.cancelCharge)}
            {' / '}
            合計: <b>{formatYen(item.amounts.orderItem.total)}</b>
          </div>
        </div>
        <div style={{ padding: '6px 10px' }}>
          <div style={{ fontSize: '11px', color: '#666' }}>EAN側</div>
          <div>
            キャンセル前: {formatYen(item.amounts.ean.amountBefore)}
            {' / '}
            キャンセル後合計: <b>{formatYen(item.amounts.ean.amountAfterTotal)}</b>
          </div>
        </div>
      </TableCell>
      <TableCell>{item.billedAt || '─'}</TableCell>
      <TableCell>{item.requestedAt}</TableCell>
      <TableCell>{item.canceledByLabel ?? '─'}</TableCell>
      <TableCell>{item.apiCanceledAt || '─'}</TableCell>
      <TableCell>{item.isInventoried ? '済' : '未'}</TableCell>
      <TableCell>{item.inventoriedBy || '─'}</TableCell>
      <TableCell>{item.inventoriedAt || '─'}</TableCell>
      <TableCell>
        {item.comments.map(comment => (
          <div key={comment.id} style={{ fontSize: '12px', marginBottom: '4px' }}>
            [{eanHotelCancelCommentTypeLabels[comment.status]}] {comment.content}
            <div style={{ color: '#999', fontSize: '11px' }}>
              {comment.commentedByName} / {comment.commentedAt}
            </div>
          </div>
        ))}
        <Button size="small" onClick={() => setShowCommentModal(true)}>
          コメント追加
        </Button>
      </TableCell>
      <TableCell>
        {item.isInventoried ? (
          <Button size="small" onClick={() => handleCancelInventory()} loading={loading}>
            取消
          </Button>
        ) : (
          <Button size="small" onClick={() => handleInventory()} loading={loading}>
            棚卸完了
          </Button>
        )}
      </TableCell>
      {showCommentModal && (
        <CancelInventoryCommentModal
          item={item}
          onClose={() => setShowCommentModal(false)}
          onSaved={() => {
            setShowCommentModal(false);
            onInventory();
          }}
        />
      )}
    </TableRow>
  );
});

export default CancelTableRow;
